import React, { useState } from 'react';
import {
 Briefcase,
 Plus,
 Edit,
 Trash2,
 CheckCircle,
 XCircle,
 MapPin,
 Clock,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { Button, SearchSelect, DataTable } from '../../components/shared';
import { useAdminJobPostings } from '../../hooks/useAdminJobPostings';
import { useToastContext } from '../../contexts/ToastContext';
import CreateJobPostingModal from '../../components/admin/CreateJobPostingModal';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminJobPostings = () => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [editingJob, setEditingJob] = useState(null);
 const { success, error: showError } = useToastContext();

 const {
 jobs,
 isLoading,
 error,
 filters,
 handleFilterChange,
 statistics,
 createJob,
 isCreating,
 updateJob,
 isUpdating,
 toggleStatus,
 isToggling,
 deleteJob,
 isDeleting
 } = useAdminJobPostings();

 const columns = [
 {
 key: 'title',
 label: 'Title',
 sortable: true,
 width: 'w-64',
 render: (job) => (
 <p className="text-sm font-medium text-ink truncate">
 {job.title || 'N/A'}
 </p>
 )
 },
 {
 key: 'department',
 label: 'Department',
 sortable: true,
 width: 'w-32',
 render: (job) => (
 <span className="text-sm text-ink capitalize">
 {job.department || 'N/A'}
 </span>
 )
 },
 {
 key: 'location',
 label: 'Location',
 sortable: true,
 width: 'w-48',
 render: (job) => (
 <span className="text-sm text-ink flex items-center gap-1">
 <MapPin className="w-4 h-4" />
 {job.location || 'N/A'}
 </span>
 )
 },
 {
 key: 'employment_type',
 label: 'Type',
 sortable: true,
 width: 'w-32',
 render: (job) => (
 <span className="text-sm text-ink capitalize">
 {job.employment_type || 'N/A'}
 </span>
 )
 },
 {
 key: 'applications_count',
 label: 'Applications',
 sortable: true,
 width: 'w-32',
 render: (job) => (
 <span className="text-sm text-ink">
 {job.applications_count || 0}
 </span>
 )
 },
 {
 key: 'is_active',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (job) => (
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ job.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800' }`}>
 {job.is_active ? 'Active' : 'Inactive'}
 </span>
 )
 }
 ];

 const actions = [
 {
 label: 'Edit',
 icon: Edit,
 variant: 'default',
 onClick: (job) => {
 setEditingJob(job);
 setShowCreateModal(true);
 }
 },
 {
 label: job => job.is_active ? 'Deactivate' : 'Activate',
 icon: job => job.is_active ? XCircle : CheckCircle,
 variant: 'default',
 onClick: (job) => {
 toggleStatus(job.id, {
 onSuccess: () => {
 success('Status Updated', `Job ${job.is_active ? 'deactivated' : 'activated'} successfully!`);
 },
 onError: (err) => showError('Status Update Failed', err.message || 'Failed to update status.')
 });
 },
 disabled: isToggling
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (job) => {
 if (window.confirm('Are you sure you want to delete this job posting?')) {
 deleteJob(job.id, {
 onSuccess: () => success('Job Deleted', 'Job posting deleted successfully!'),
 onError: (err) => showError('Delete Failed', err.message || 'Failed to delete job.')
 });
 }
 },
 disabled: isDeleting
 }
 ];

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Job Postings"
 subtitle="Manage job postings and career opportunities"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
 <h3 className="text-xl font-bold text-ink mb-2">Error Loading Job Postings</h3>
 <p className="text-ink-secondary">
 {error?.response?.data?.message || 'Failed to load job postings. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Job Postings"
 subtitle="Manage job postings and career opportunities"
 actions={
 <Button
 className="rounded-md flex items-center gap-2"
 onClick={() => {
 setEditingJob(null);
 setShowCreateModal(true);
 }}
 >
 <Plus className="w-4 h-4" />
 Create Job Posting
 </Button>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.jobPostings.title}
 steps={ADMIN_PAGE_GUIDES.jobPostings.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Jobs</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{statistics.total_jobs || 0}</p>
 </div>
 <Briefcase className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Active</p>
 <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">{statistics.active_jobs || 0}</p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Inactive</p>
 <p className="text-2xl font-bold text-ink-secondary mt-1 tabular-nums">{statistics.inactive_jobs || 0}</p>
 </div>
 <XCircle className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Applications</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{statistics.total_applications || 0}</p>
 </div>
 <Clock className="w-8 h-8 text-ink-secondary" />
 </div>
 </div>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
 <SearchSelect
 label="Status"
 value={filters.is_active}
 onChange={(value) => handleFilterChange('is_active', value)}
 options={[
 { value: '', label: 'All Statuses' },
 { value: 'true', label: 'Active' },
 { value: 'false', label: 'Inactive' }
 ]}
 />
 <SearchSelect
 label="Department"
 value={filters.department}
 onChange={(value) => handleFilterChange('department', value)}
 options={[
 { value: '', label: 'All Departments' },
 { value: 'Engineering', label: 'Engineering' },
 { value: 'Design', label: 'Design' },
 { value: 'Operations', label: 'Operations' },
 { value: 'Marketing', label: 'Marketing' },
 { value: 'Sales', label: 'Sales' },
 { value: 'Analytics', label: 'Analytics' }
 ]}
 />
 <div className="flex items-end">
 <input
 type="text"
 placeholder="Search jobs..."
 value={filters.search || ''}
 onChange={(e) => handleFilterChange('search', e.target.value)}
 className="w-full px-4 py-2 border border-border rounded-xl text-sm bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-ink focus:border-ink"
 />
 </div>
 <div className="flex items-end">
 <Button
 onClick={() => {
 handleFilterChange('is_active', '');
 handleFilterChange('department', '');
 handleFilterChange('search', '');
 }}
 variant="outline"
 className="w-full h-[42px] rounded-md"
 >
 Reset Filters
 </Button>
 </div>
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading job postings…</p>
 </div>
 ) : (
 <DataTable
 data={jobs}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search job postings..."
 onSearch={(searchTerm) => handleFilterChange('search', searchTerm)}
 emptyMessage="No job postings found"
 pageSize={10}
 showPagination={true}
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>

 {showCreateModal && (
 <CreateJobPostingModal
 isOpen={showCreateModal}
 onClose={() => {
 setShowCreateModal(false);
 setEditingJob(null);
 }}
 onSave={(data) => {
 if (editingJob) {
 updateJob(
 { id: editingJob.id, data },
 {
 onSuccess: () => {
 success('Job Updated', 'Job posting updated successfully!');
 setShowCreateModal(false);
 setEditingJob(null);
 },
 onError: (err) => showError('Update Failed', err.message || 'Failed to update job.')
 }
 );
 } else {
 createJob(data, {
 onSuccess: () => {
 success('Job Created', 'Job posting created successfully!');
 setShowCreateModal(false);
 setEditingJob(null);
 },
 onError: (err) => showError('Creation Failed', err.message || 'Failed to create job.')
 });
 }
 }}
 job={editingJob}
 isEdit={!!editingJob}
 isLoading={isCreating || isUpdating}
 />
 )}
 </div>
 );
};

export default AdminJobPostings;
